import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Button, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Box,
  Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Alert, Snackbar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

// Stílus a fájl feltöltéshez
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const RouteList = () => {
  const [routeLines, setRouteLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { getAuthHeaders, hasPermission } = useAuth();
  
  // Jogosultság ellenőrzés - szerkesztéshez kell a canManageRoutes jogosultság
  const canEdit = hasPermission('canManageRoutes');

  // Vonalak betöltése
  const loadRouteLines = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/RouteLine', { headers: getAuthHeaders() });
      setRouteLines(response.data);
      setError(null);
    } catch (err) {
      console.error('Hiba a vonalak betöltésekor:', err);
      setError('Nem sikerült betölteni a vonalakat. Kérjük, próbálja újra később.');
    } finally {
      setLoading(false);
    }
  };

  // Komponens betöltésekor vonalak lekérése
  useEffect(() => {
    loadRouteLines();
  }, []);

  // Fájl kiválasztás kezelése
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
        console.log('Fájl kiválasztva:', file.name, 'Méret:', file.size, 'byte');
        setSelectedFile(file);
      } else {
        setSnackbar({
          open: true,
          message: 'Csak Excel vagy CSV fájlokat (.xlsx, .xls, .csv) lehet feltölteni!',
          severity: 'error'
        });
        setSelectedFile(null);
      }
    }
  };

  // Excel/CSV fájl importálása
  const handleImport = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);

    // Diagnosztikai célokból kilistázzuk a formData tartalmát
    console.log('Form adatok:', selectedFile.name, selectedFile.type, selectedFile.size);

    try {
      setUploading(true);
      console.log('Importálás kezdése...');
      
      const response = await axios.post('/api/RouteLine/import', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        // Hosszabb timeout beállítása
        timeout: 30000
      });

      console.log('Sikeres válasz:', response.data);
      
      setSnackbar({
        open: true,
        message: `Nyomvonalak sikeresen importálva! ${response.data?.count || 0} sor feldolgozva.`,
        severity: 'success'
      });
      setImportDialogOpen(false);
      setSelectedFile(null);
      loadRouteLines(); // Frissítjük a listát
    } catch (err) {
      console.error('Hiba az importálás során:', err);
      console.error('Hiba részletei:', err.response?.data || 'Nincs válasz adat');
      
      setSnackbar({
        open: true,
        message: 'Hiba történt az importálás során: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Vonalak
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Excel importálása
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : routeLines.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">Nincsenek nyomvonalak az adatbázisban.</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Kattintson az "Excel importálása" gombra nyomvonalak feltöltéséhez.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Név</TableCell>
                <TableCell>Nyomvonal kód</TableCell>
                <TableCell>Vonalszám</TableCell>
                <TableCell>Létrehozva</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {routeLines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell>{line.id}</TableCell>
                  <TableCell>{line.name}</TableCell>
                  <TableCell>{line.routeCode}</TableCell>
                  <TableCell>{line.lineNumber}</TableCell>
                  <TableCell>{new Date(line.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => !uploading && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Excel fájl importálása</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              Válasszon ki egy Excel fájlt (.xlsx, .xls) a nyomvonalak importálásához.
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Az Excel fájlnak tartalmaznia kell a következő oszlopokat: név, nyomvonal kód, vonalszám.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <a href="#" onClick={(e) => {
                e.preventDefault();
                const header = ['Név', 'Nyomvonal kód', 'Vonalszám'];
                const data = [
                  ['2 Autóbusz-állomás - Kórház', '02_NOA', '2'],
                  ['2 Kórház - Autóbusz-állomás', '02_NVA', '2'],
                  ['2A Autóbusz-állomás - Kórház', '02ANOA', '2A'],
                  ['2A Kórház - Autóbusz-állomás', '02ANVA', '2A']
                ];
                
                // CSV formátum generálása
                const csvContent = [
                  header.join(','),
                  ...data.map(row => row.join(','))
                ].join('\n');
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', 'nyomvonalak_minta.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                Minta CSV fájl letöltése
              </a>
            </Typography>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
              >
                Fájl kiválasztása
                <VisuallyHiddenInput type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
              </Button>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Kiválasztott fájl: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={uploading}>Mégsem</Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary" 
            disabled={!selectedFile || uploading}
            startIcon={uploading && <CircularProgress size={20} color="inherit" />}
          >
            {uploading ? 'Importálás...' : 'Importálás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar értesítések */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RouteList;
